import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container, Box, Typography, Paper, Grid, TextField, Button,
  Switch, FormControlLabel, Divider, Snackbar, Alert, Card,
  CardContent, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Select, MenuItem, FormControl, InputLabel, CircularProgress,
  useMediaQuery, useTheme
} from '@mui/material';
import {
  Settings, Save, Receipt, CheckCircle, Error as ErrorIcon,
  CloudUpload, ContentCopy, LocalDrink, PlayArrow, Sync
} from '@mui/icons-material';
import { MenuInicial } from "./MenuInicial";
import { API_BASE_URL, FACTURACION_GET, FACTURACION_SAVE, FACTURACION_TEST } from '../services/apiConfig';
import { isAuthenticated } from '../services/api.js';

const TIPOS_COMPROBANTE = [
  { id: 1, nombre: 'Factura A' },
  { id: 2, nombre: 'Nota de Débito A' },
  { id: 3, nombre: 'Nota de Crédito A' },
  { id: 6, nombre: 'Factura B' },
  { id: 7, nombre: 'Nota de Débito B' },
  { id: 8, nombre: 'Nota de Crédito B' },
  { id: 11, nombre: 'Factura C' },
  { id: 12, nombre: 'Nota de Débito C' },
  { id: 13, nombre: 'Nota de Crédito C' },
  { id: 15, nombre: 'Factura E' },
];

const CONDICIONES_IVA = [
  { id: '1', nombre: 'Responsable Inscripto' },
  { id: '2', nombre: 'Responsable no Inscripto' },
  { id: '3', nombre: 'Responsable no Inscripto - Born' },
  { id: '4', nombre: 'Exento' },
  { id: '5', nombre: 'Consumidor Final' },
  { id: '6', nombre: 'Remitente' },
  { id: '9', nombre: 'Monotributista' },
];

function FacturacionConfig() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const userData = JSON.parse(sessionStorage.getItem("userData"));
  const perfil = userData?.perfil;

  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [config, setConfig] = useState({
    habilitado: false,
    access_token: '',
    punto_venta: 1,
    tipo_comprobante_predeterminado: 6,
    razon_social: '',
    cuit: '',
    domicilio: '',
    condicion_iva: '5',
    condiciones_iva_por_defecto: '5',
    ambiente: 'dev',
    mostrar_preview: true,
    auto_imprimir: false,
  });

  useEffect(() => {
    if (perfil !== 1) {
      setSnackbar({ open: true, message: 'Solo administradores pueden acceder', severity: 'error' });
      return;
    }
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_BASE_URL + FACTURACION_GET);
      if (response.data.success && response.data.data) {
        setConfig({ ...config, ...response.data.data });
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    } finally {
      setLoading(false);
    }
  };

  const guardarConfig = async () => {
    try {
      setGuardando(true);
      const response = await axios.post(API_BASE_URL + FACTURACION_SAVE, {
        ...config,
        habilitado: config.habilitado ? 1 : 0,
        mostrar_preview: config.mostrar_preview ? 1 : 0,
        auto_imprimir: config.auto_imprimir ? 1 : 0,
      });
      
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Configuración guardada exitosamente', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: response.data.message || 'Error al guardar', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error de conexión', severity: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const handleChange = (campo, valor) => {
    setConfig({ ...config, [campo]: valor });
  };

  const copiarToken = () => {
    navigator.clipboard.writeText(config.access_token);
    setSnackbar({ open: true, message: 'Token copiado al portapapeles', severity: 'info' });
  };

  const probarConexion = async () => {
    if (!config.access_token) {
      setSnackbar({ open: true, message: 'Primero ingresa el Access Token', severity: 'warning' });
      return;
    }

    try {
      setProbando(true);
      setTestResult(null);
      setTestDialogOpen(true);
      
      const response = await axios.post(API_BASE_URL + FACTURACION_TEST, {
        punto_venta: config.punto_venta
      });
      
      if (response.data.success) {
        setTestResult({
          success: true,
          message: response.data.message,
          data: response.data.data
        });
      } else {
        setTestResult({
          success: false,
          message: response.data.message,
          data: null
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Error de conexión: ' + (error.response?.data?.message || error.message),
        data: null
      });
    } finally {
      setProbando(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth={isMobile ? false : "xl"} sx={{ px: isMobile ? 1 : 3 }}>
        <MenuInicial />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={isMobile ? false : "xl"} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      
      <Box sx={{ my: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Receipt sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                Configuración Facturación ARCA
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configura la emisión de comprobantes electrónicos (CAE)
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Estado del Servicio
                    </Typography>
                    <Chip
                      icon={config.habilitado ? <CheckCircle /> : <ErrorIcon />}
                      label={config.habilitado ? 'Habilitado' : 'Deshabilitado'}
                      color={config.habilitado ? 'success' : 'default'}
                    />
                  </Box>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.habilitado}
                        onChange={(e) => handleChange('habilitado', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Habilitar facturación electrónica"
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Entorno
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ambiente</InputLabel>
                    <Select
                      value={config.ambiente}
                      label="Ambiente"
                      onChange={(e) => handleChange('ambiente', e.target.value)}
                    >
                      <MenuItem value="dev">Desarrollo (Testing)</MenuItem>
                      <MenuItem value="prod">Producción</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    En desarrollo puedes usar el CUIT de prueba sin certificado.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Credenciales AFIP SDK
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={probando ? <Sync className="spin" /> : <PlayArrow />}
                      onClick={probarConexion}
                      disabled={probando || !config.access_token}
                    >
                      {probando ? 'Probando...' : 'Probar Conexión'}
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Obtén tu access_token en{' '}
                    <a href="https://app.afipsdk.com" target="_blank" rel="noopener noreferrer">
                      app.afipsdk.com
                    </a>
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      label="Access Token"
                      fullWidth
                      size="small"
                      value={config.access_token}
                      onChange={(e) => handleChange('access_token', e.target.value)}
                      placeholder="eyJhbGciOiJIUzI1NiIsInR..."
                    />
                    <IconButton onClick={copiarToken} color="primary" sx={{ mt: 0.5 }}>
                      <ContentCopy />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Datos del Emisor
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Razón Social"
                        fullWidth
                        size="small"
                        value={config.razon_social}
                        onChange={(e) => handleChange('razon_social', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="CUIT (sin guiones)"
                        fullWidth
                        size="small"
                        value={config.cuit}
                        onChange={(e) => handleChange('cuit', e.target.value)}
                        placeholder="20123456789"
                        inputProps={{ maxLength: 11 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Punto de Venta"
                        type="number"
                        fullWidth
                        size="small"
                        value={config.punto_venta}
                        onChange={(e) => handleChange('punto_venta', parseInt(e.target.value) || 1)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Domicilio Comercial"
                        fullWidth
                        size="small"
                        value={config.domicilio}
                        onChange={(e) => handleChange('domicilio', e.target.value)}
                        placeholder="Av. Corrientes 1234, CABA"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Condición ante IVA</InputLabel>
                        <Select
                          value={config.condicion_iva}
                          label="Condición ante IVA"
                          onChange={(e) => handleChange('condicion_iva', e.target.value)}
                        >
                          {CONDICIONES_IVA.map((cond) => (
                            <MenuItem key={cond.id} value={cond.id}>{cond.nombre}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Configuración de Comprobantes
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Tipo de Comprobante Predeterminado</InputLabel>
                        <Select
                          value={config.tipo_comprobante_predeterminado}
                          label="Tipo de Comprobante Predeterminado"
                          onChange={(e) => handleChange('tipo_comprobante_predeterminado', e.target.value)}
                        >
                          {TIPOS_COMPROBANTE.map((tipo) => (
                            <MenuItem key={tipo.id} value={tipo.id}>{tipo.nombre}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Condición IVA por defecto (cliente)</InputLabel>
                        <Select
                          value={config.condiciones_iva_por_defecto}
                          label="Condición IVA por defecto (cliente)"
                          onChange={(e) => handleChange('condiciones_iva_por_defecto', e.target.value)}
                        >
                          {CONDICIONES_IVA.map((cond) => (
                            <MenuItem key={cond.id} value={cond.id}>{cond.nombre}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.mostrar_preview}
                            onChange={(e) => handleChange('mostrar_preview', e.target.checked)}
                          />
                        }
                        label="Mostrar preview antes de facturar"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.auto_imprimir}
                            onChange={(e) => handleChange('auto_imprimir', e.target.checked)}
                          />
                        }
                        label="Auto-imprimir comprobante después de facturar"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={fetchConfig}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={guardando ? <CircularProgress size={20} /> : <Save />}
                  onClick={guardarConfig}
                  disabled={guardando || !config.access_token}
                >
                  {guardando ? 'Guardando...' : 'Guardar Configuración'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            <LocalDrink sx={{ mr: 1, verticalAlign: 'middle' }} />
            Tipos de Comprobantes Disponibles
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Código</strong></TableCell>
                  <TableCell><strong>Tipo</strong></TableCell>
                  <TableCell><strong>Uso</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>1, 2, 3</TableCell>
                  <TableCell>A</TableCell>
                  <TableCell>Para responsables inscriptos (requiere CUIT cliente)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>6, 7, 8</TableCell>
                  <TableCell>B</TableCell>
                  <TableCell>Para consumidores finales / monotributistas</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>11, 12, 13</TableCell>
                  <TableCell>C</TableCell>
                  <TableCell>Para monotributistas (sin IVA)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>15</TableCell>
                  <TableCell>E</TableCell>
                  <TableCell>Para exportaciones</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {testResult?.success ? (
            <CheckCircle color="success" />
          ) : (
            <ErrorIcon color="error" />
          )}
          Prueba de Conexión ARCA
        </DialogTitle>
        <DialogContent>
          {probando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Conectando con ARCA...</Typography>
            </Box>
          ) : testResult ? (
            <Box>
              <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                {testResult.message}
              </Alert>
              {testResult.success && testResult.data && (
                <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>Punto de Venta:</strong> {testResult.data.punto_venta}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Último Comprobante:</strong> #{testResult.data.ultimo_numero}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Ambiente:</strong> {testResult.data.ambiente === 'dev' ? 'Desarrollo' : 'Producción'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Fecha:</strong> {testResult.data.fecha}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography>Esperando resultado...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}

export default FacturacionConfig;
